import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * The chrome every dashboard page sits in.
 *
 * FleetTab gets its composed feel from one layout shared by every screen, so
 * padding, scroll behaviour and vertical rhythm are decided once. Routly had
 * the same markup copied into ten pages, and it had drifted — `p-6` here,
 * `py-8 px-4` there, `space-y-6` on some pages and per-element margins on
 * others — which is what made moving between screens feel like moving between
 * apps.
 *
 * `PageShell` owns the outer frame; `PageBody` owns the scrolling content area
 * and the space between the sections inside it.
 *
 * Both applications render it. The console used to put `p-6` on its own `main`
 * and `space-y-6` inside each page, so a screen there carried half again the
 * padding of the same screen in the product.
 *
 * It fills the height the app shell gives it rather than claiming the viewport:
 * with `min-h-screen` the page would be a screen tall *below* the top bar, so
 * every screen scrolled by the height of that bar with nothing at the bottom.
 */

interface PageShellProps {
  children: ReactNode;
  className?: string;
}

export function PageShell({ children, className }: PageShellProps) {
  return <div className={cn('flex h-full flex-col bg-background', className)}>{children}</div>;
}

interface PageBodyProps {
  children: ReactNode;
  /**
   * Constrains the content to a readable measure. Use for forms and settings;
   * leave off for tables and maps, which want the full width.
   */
  narrow?: boolean;
  /**
   * The page stops scrolling and its last child takes the height that is left.
   *
   * For a screen whose subject is one long list. Scrolling the whole page means
   * the filters, the column names and the pager all leave the screen together,
   * so by row thirty the reader has eight unlabelled columns and no way to
   * change the query without scrolling back up. With `fill`, the chrome holds
   * still and only the rows move — which is also what `Table stickyHeader`
   * needs, since `position: sticky` has to have something that scrolls to
   * stick to.
   *
   * The child that fills marks itself `md:min-h-0 md:flex-1`; `min-h-0` is what
   * stops a flex item refusing to shrink below its content and pushing the
   * pager off the bottom instead of scrolling.
   *
   * From `md` up only, and the breakpoint is not cosmetic. Below it the same
   * table renders as a list of cards, and pinning a filter row and a pager to
   * a 710px viewport spends a sixth of the screen on chrome that a phone
   * scrolls past in one flick anyway. It is also the difference between a page
   * that scrolls and one that does not: `fill` puts `overflow-hidden` on this
   * element, so applied unconditionally it left the trip logbook showing two
   * of its sixty trips with no way to reach the rest. Phones keep page scroll.
   */
  fill?: boolean;
  className?: string;
}

export function PageBody({ children, narrow = false, fill = false, className }: PageBodyProps) {
  return (
    <main
      className={cn(
        'flex-1 px-4 py-4 sm:px-5 sm:py-5',
        // Every screen is a fresh component, so this runs once per navigation
        // and never on a re-render within a page. It is what stops a route
        // change from being an instantaneous swap of two dense tables, which
        // reads as a glitch rather than as movement.
        'animate-enter',
        fill ? 'overflow-auto md:flex md:min-h-0 md:flex-col md:overflow-hidden' : 'overflow-auto',
        // One rule for the space between sections, rather than each page
        // choosing its own margins.
        '[&>*+*]:mt-4',
        narrow && 'mx-auto w-full max-w-4xl',
        className
      )}
    >
      {children}
    </main>
  );
}
